import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Award, MapPin, Building2, Code } from 'lucide-react';

const ExperienceSection = () => {
  const experiences = [
    {
      role: 'Managing Director',
      company: 'Radiance Education',
      type: 'Full-time',
      period: 'Nov 2023 - Present',
      duration: '2 yrs 3 mos',
      location: 'Bangladesh',
      highlight: 'British Council Top Performer 2024-26 • 4,000+ students',
      icon: Award,
      accentColor: 'from-red-500 to-rose-500',
    },
    {
      role: 'Founder & CEO',
      company: 'VirtualSwift',
      type: 'Self-employed',
      period: 'Jan 2019 - Present',
      duration: '7 yrs',
      location: 'Bangladesh',
      highlight: 'Digital agency • Web & AI solutions globally',
      icon: Briefcase,
      accentColor: 'from-rose-500 to-pink-500',
    },
    {
      role: 'Executive Director',
      company: 'Thousand Hopes Inc.',
      type: 'Full-time',
      period: 'May 2023 - Sep 2023',
      duration: '5 mos',
      location: 'Toronto, Canada',
      highlight: 'Team Leadership & Strategic Planning',
      icon: Building2,
      accentColor: 'from-orange-500 to-red-500',
    },
    {
      role: 'Frontend Developer',
      company: 'Apricot IT',
      type: 'Part-time',
      period: '2019 - 2021',
      duration: '2 yrs',
      location: 'London, UK',
      highlight: 'Frontend development & UI implementation',
      icon: Code,
      accentColor: 'from-pink-500 to-rose-500',
    },
    {
      role: 'Senior Web Developer',
      company: 'Catalystt IT Solutions',
      type: 'Part-time',
      period: '2019 - 2021',
      duration: '2 yrs',
      location: 'Bangladesh',
      highlight: 'Full-stack development & mentorship',
      icon: MapPin,
      accentColor: 'from-red-500 to-orange-500',
    },
  ];

  return (
    <section id="experience" className="py-20 lg:py-28 relative">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      {/* Light theme decorative elements */}
      <div className="light-decor-blob-1" style={{ top: '5%', right: '-100px', width: '320px', height: '320px' }} />
      <div className="light-decor-blob-3" style={{ bottom: '20%', left: '3%', width: '200px', height: '200px' }} />
      <div className="light-decor-circle-2" style={{ top: '40%', right: '3%', width: '80px', height: '80px' }} />
      <div className="light-decor-dot-2" style={{ top: '15%', left: '20%' }} />
      <div className="light-decor-line-2" style={{ top: '8%', right: '15%', width: '55px' }} />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="text-sm font-medium text-primary uppercase tracking-wider">Experience</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-3 mb-4">
            Career & <span className="text-gradient">Impact</span>
          </h2>
          <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto font-medium">
            A track record of building systems that deliver real results
          </p>
        </motion.div>

        {/* Grid Layout - 2 columns on desktop, 1 on mobile */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 max-w-5xl mx-auto">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -4, scale: 1.01 }}
              className={`glass-strong rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all cursor-default ${
                index === experiences.length - 1 && experiences.length % 2 !== 0 ? 'lg:col-span-2 lg:max-w-[50%] lg:mx-auto' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${exp.accentColor} flex items-center justify-center flex-shrink-0`}>
                  <exp.icon className="w-5 h-5 text-white" />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="text-base font-bold text-foreground truncate">{exp.role}</h3>
                    <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">{exp.period}</span>
                  </div>
                  <p className="text-base text-muted-foreground mb-1 font-medium">
                    {exp.company} <span className="text-muted-foreground/60">• {exp.location}</span>
                  </p>
                  <p className="text-sm text-muted-foreground/80 font-medium">{exp.highlight}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { ExperienceSection };
